<template>
  <div id="app" class="min-h-screen bg-gray-50 font-sans text-slate-900 flex flex-col">
    
    <header class="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          
          <div class="flex items-center shrink-0">
            <router-link to="/" class="flex items-center group">
              <svg class="h-8 w-8 text-indigo-600 transition-colors group-hover:text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 15l6-6 4 4 8-8" />
              </svg>
              <span class="ml-2 text-xl font-bold text-gray-900 tracking-tight group-hover:text-black">SinceInception</span>
            </router-link>
          </div>

          <nav class="hidden md:flex items-center space-x-1" aria-label="Progress">
            <template v-for="(step, index) in steps" :key="step.name">
              
              <div 
                class="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors"
                :class="getStepClass(index)"
              >
                <svg v-if="index < currentStepIndex" class="w-4 h-4 mr-2 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                
                <span v-else 
                  class="w-5 h-5 flex items-center justify-center rounded-full text-xs mr-2 border"
                  :class="index === currentStepIndex ? 'border-indigo-600 text-indigo-600 font-bold' : 'border-gray-300 text-gray-400'"
                >
                  {{ index + 1 }}
                </span>

                {{ step.label }}
              </div>

              <svg v-if="index < steps.length - 1" class="w-4 h-4 text-gray-300 mx-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </template>
          </nav>

          <div class="md:hidden text-sm font-medium text-gray-500">
            <span v-if="currentStepIndex >= 0">
              Step <span class="text-indigo-600 font-bold">{{ currentStepIndex + 1 }}</span> of {{ steps.length }}
            </span>
          </div>

        </div>
      </div>
    </header>

    <main class="flex-grow">
      <router-view />
    </main>

    <footer class="bg-white border-t border-gray-200 py-10 mt-auto">
      <div class="max-w-5xl mx-auto px-6 text-center space-y-6">
        
        <p class="text-xs text-gray-400 leading-relaxed max-w-4xl mx-auto">
          <strong>Not Investment Advice:</strong> SinceInception is an educational tool designed for informational purposes only. We are not a registered investment advisor, broker, or dealer. The calculations, benchmarks, and suggestions provided are hypothetical and based on historical data; they do not constitute personalized financial planning or investment recommendations.
        </p>

        <p class="text-xs text-gray-400 leading-relaxed max-w-4xl mx-auto">
          <strong>Data Accuracy & Performance:</strong> Benchmark returns are based on index or ETF data and do not reflect trading fees, taxes, or management expenses that would lower actual returns. Past performance is not indicative of future results. User calculations rely on the accuracy of the data you provide. We do not guarantee the accuracy of third-party market data.
        </p>

        <div class="pt-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-500">
          <p>&copy; {{ new Date().getFullYear() }} SinceInception. All rights reserved.</p>
          <div class="flex space-x-6">
            <a href="#" class="hover:text-gray-900 transition-colors">Privacy Policy</a>
            <a href="#" class="hover:text-gray-900 transition-colors">Terms of Use</a>
          </div>
        </div>

      </div>
    </footer>

  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

// Define the linear journey
const steps = [
  { name: 'RiskSelection', label: 'Benchmark' },
  { name: 'PortfolioForm', label: 'Inputs' },
  { name: 'PerformanceCompare', label: 'Results' },
  { name: 'ResultsNextSteps', label: 'Plan' },
];

// Determine where we are
const currentStepIndex = computed(() => {
  if (route.name === 'Home') return -1;
  return steps.findIndex(s => s.name === route.name);
});

// Styling logic
function getStepClass(index) {
  const current = currentStepIndex.value;
  if (index < current) {
    return 'text-gray-900'; // Completed
  } else if (index === current) {
    return 'text-indigo-600 bg-indigo-50'; // Active
  } else {
    return 'text-gray-400'; // Future
  }
}
</script>

<style>
@import "@/assets/main.css";

/* Ensure stickiness works smoothly */
header {
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.95);
}
</style>